import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ResponsiveTable from './ResponsiveTable';
import SelectField from './inputs/SelectField';
import AutocompleteField from './inputs/AutocompleteField';
import SpecialRequestFormFields from './specialRequests/SpecialRequestFormFields';
import SpecialRequestDetailView from './specialRequests/SpecialRequestDetailView';
import SpecialRequestFulfillDialog from './specialRequests/SpecialRequestFulfillDialog';
import { useFeedback } from '../context/FeedbackContext';
import { useOpenOrdersCount } from '../context/OpenOrdersCountContext';
import { useAsyncAction } from '../hooks/useAsyncAction';
import {
  ALL_SPECIAL_REQUEST_CATEGORIES,
  ALL_SPECIAL_REQUEST_STATUSES,
  SPECIAL_REQUEST_STATUSES,
} from '../constants/appConstants';
import { ROLES } from '../constants/roles';
import { formatDateTime } from '../utils/dateFormat';
import {
  DEFAULT_SPECIAL_REQUEST_FORM,
  buildOrderDescriptionFromRequest,
  buildSpecialRequestPayload,
  formatSpecialRequestOrderRef,
  getSpecialRequestOrderId,
  validateSpecialRequestForm,
} from '../utils/specialRequestDisplay';

function getStatusColor(status) {
  switch (status) {
    case 'submitted': return 'default';
    case 'inProgress': return 'info';
    case 'ready': return 'success';
    case 'denied': return 'error';
    case 'fulfilled': return 'success';
    default: return 'default';
  }
}

const FULFILLABLE_STATUSES = new Set([
  SPECIAL_REQUEST_STATUSES.IN_PROGRESS,
  SPECIAL_REQUEST_STATUSES.READY,
]);

export default function SpecialRequestsStaffTab() {
  const { t } = useTranslation();
  const { showError, showSuccess } = useFeedback();
  const { refreshOpenOrdersCount } = useOpenOrdersCount();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [clientFilter, setClientFilter] = useState(null);
  const [clients, setClients] = useState([]);
  const [denyTarget, setDenyTarget] = useState(null);
  const [denyNote, setDenyNote] = useState('');
  const [fulfillTarget, setFulfillTarget] = useState(null);
  const [detail, setDetail] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createClient, setCreateClient] = useState(null);
  const [form, setForm] = useState({ ...DEFAULT_SPECIAL_REQUEST_FORM });
  const [filterOptions, setFilterOptions] = useState({ shapes: [], colors: [], clarities: [] });
  const { loading: actionLoading, run: runAction } = useAsyncAction();
  const { loading: saving, run: runSave } = useAsyncAction();

  const statusOptions = useMemo(() => [
    { value: '', label: t('orders.all') },
    ...ALL_SPECIAL_REQUEST_STATUSES.map((value) => ({
      value,
      label: t(`specialRequests.statuses.${value}`),
    })),
  ], [t]);

  const categoryOptions = useMemo(() => [
    { value: '', label: t('orders.all') },
    ...ALL_SPECIAL_REQUEST_CATEGORIES.map((value) => ({
      value,
      label: t(`specialRequests.categories.${value}`),
    })),
  ], [t]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get('/api/users', { params: { role: ROLES.CLIENT, limit: 500 } });
      const list = Array.isArray(res.data) ? res.data : (res.data?.users || []);
      setClients(list.filter((u) => !u.disabled));
    } catch {
      setClients([]);
    }
  }, []);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const res = await axios.get('/api/diamonds/filters/options');
      setFilterOptions({
        shapes: res.data?.certifiedShapes || res.data?.shapes || [],
        certifiedShapes: res.data?.certifiedShapes || res.data?.shapes || [],
        lotShapes: res.data?.lotShapes || [],
        colors: res.data?.colors || [],
        clarities: res.data?.clarities || [],
      });
    } catch {
      setFilterOptions({ shapes: [], colors: [], clarities: [] });
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (clientFilter?._id) params.clientId = clientFilter._id;
      const res = await axios.get('/api/special-requests', { params });
      setRequests(res.data.specialRequests || []);
    } catch (err) {
      showError(err.response?.data?.error || t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, clientFilter, showError, t]);

  useEffect(() => {
    fetchClients();
    fetchFilterOptions();
  }, [fetchClients, fetchFilterOptions]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const updateStatus = async (request, status, statusNote) => {
    await runAction(async () => {
      try {
        await axios.patch(`/api/special-requests/${request._id}/status`, { status, statusNote });
        await fetchRequests();
        refreshOpenOrdersCount();
      } catch (err) {
        showError(err.response?.data?.error || t('common.error'));
      }
    });
  };

  const handleDeny = async () => {
    if (!denyTarget) return;
    if (!denyNote.trim()) {
      showError(t('specialRequests.denyNoteRequired'));
      return;
    }
    await updateStatus(denyTarget, SPECIAL_REQUEST_STATUSES.DENIED, denyNote.trim());
    setDenyTarget(null);
    setDenyNote('');
  };

  const resetForm = () => {
    setForm({ ...DEFAULT_SPECIAL_REQUEST_FORM, details: { ...DEFAULT_SPECIAL_REQUEST_FORM.details } });
    setCreateClient(null);
  };

  const handleCreate = async () => {
    if (!createClient?._id) {
      showError(t('specialRequests.validation.clientRequired'));
      return;
    }
    const validationError = validateSpecialRequestForm(form, t);
    if (validationError) {
      showError(validationError);
      return;
    }
    await runSave(async () => {
      await axios.post(
        '/api/special-requests',
        buildSpecialRequestPayload(form, { clientId: createClient._id }),
      );
      showSuccess(t('specialRequests.createdForClient'));
      setCreateOpen(false);
      resetForm();
      await fetchRequests();
      refreshOpenOrdersCount();
    });
  };

  const handleFulfill = async (pricingPayload) => {
    if (!fulfillTarget) return;
    await runAction(async () => {
      try {
        await axios.post(`/api/special-requests/${fulfillTarget._id}/fulfill`, pricingPayload);
        showSuccess(t('specialRequests.fulfillSuccess'));
        setFulfillTarget(null);
        await fetchRequests();
        refreshOpenOrdersCount();
      } catch (err) {
        showError(err.response?.data?.error || t('common.error'));
        throw err;
      }
    });
  };

  const columns = [
    {
      id: 'client',
      label: t('orders.client'),
      render: (row) => {
        const u = row.clientUser;
        if (!u) return '—';
        return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '—';
      },
    },
    { id: 'category', label: t('specialRequests.category'), render: (row) => t(`specialRequests.categories.${row.category}`) },
    {
      id: 'description',
      label: t('specialRequests.summary'),
      render: (row) => (
        <Typography
          variant="body2"
          noWrap
          sx={{ maxWidth: 240, cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'divider' }}
          onClick={() => setDetail(row)}
        >
          {buildOrderDescriptionFromRequest(row)}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: t('orders.status'),
      render: (row) => (
        <Chip size="small" label={t(`specialRequests.statuses.${row.status}`)} color={getStatusColor(row.status)} />
      ),
    },
    { id: 'createdAt', label: t('orders.date'), render: (row) => formatDateTime(row.createdAt) },
    {
      id: 'order',
      label: t('specialRequests.linkedOrder'),
      render: (row) => {
        const orderId = getSpecialRequestOrderId(row);
        if (!orderId) return '—';
        return (
          <Typography
            component={Link}
            to={`/orders/${orderId}`}
            variant="body2"
            sx={{ fontWeight: 600, textDecoration: 'none', color: 'primary.main' }}
            onClick={(e) => e.stopPropagation()}
          >
            #{formatSpecialRequestOrderRef(row.convertedOrder)}
          </Typography>
        );
      },
    },
    {
      id: 'actions',
      label: t('common.actions'),
      stickyRight: true,
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {row.status === SPECIAL_REQUEST_STATUSES.SUBMITTED && (
            <Button size="small" onClick={() => updateStatus(row, SPECIAL_REQUEST_STATUSES.IN_PROGRESS)}>
              {t('specialRequests.actions.markInProgress')}
            </Button>
          )}
          {FULFILLABLE_STATUSES.has(row.status) && (
            <Button size="small" variant="contained" onClick={() => setFulfillTarget(row)}>
              {t('specialRequests.actions.createOrder')}
            </Button>
          )}
          {(row.status === SPECIAL_REQUEST_STATUSES.SUBMITTED || row.status === SPECIAL_REQUEST_STATUSES.IN_PROGRESS) && (
            <Button size="small" color="error" onClick={() => { setDenyTarget(row); setDenyNote(''); }}>
              {t('specialRequests.actions.deny')}
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          {t('specialRequests.createForClient')}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <SelectField
          label={t('orders.status')}
          value={statusFilter}
          onChange={setStatusFilter}
          options={statusOptions}
          size="small"
          sx={{ minWidth: 180 }}
        />
        <SelectField
          label={t('specialRequests.category')}
          value={categoryFilter}
          onChange={setCategoryFilter}
          options={categoryOptions}
          size="small"
          sx={{ minWidth: 180 }}
        />
        <AutocompleteField
          label={t('orders.client')}
          options={clients}
          value={clientFilter?._id ?? ''}
          onChange={(id) => setClientFilter(clients.find((c) => c._id === id) || null)}
          getOptionLabel={(opt) => `${opt.firstName || ''} ${opt.lastName || ''}`.trim() || opt.email || ''}
          getOptionValue={(u) => u._id}
          isOptionEqualToValue={(opt, val) => opt._id === val._id}
          size="small"
          sx={{ minWidth: 220 }}
        />
      </Box>

      <ResponsiveTable
        columns={columns}
        data={requests}
        loading={loading}
        emptyMessage={t('specialRequests.emptyStaff')}
        onRowClick={(row) => setDetail(row)}
        renderMobileCard={(row) => (
          <>
            <Typography variant="subtitle2" fontWeight={600}>
              {`${row.clientUser?.firstName || ''} ${row.clientUser?.lastName || ''}`.trim()}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.75, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                {formatDateTime(row.createdAt)}
              </Typography>
              <Chip size="small" label={t(`specialRequests.statuses.${row.status}`)} color={getStatusColor(row.status)} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
              {buildOrderDescriptionFromRequest(row)}
            </Typography>
          </>
        )}
      />

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('specialRequests.detailTitle')}</DialogTitle>
        <DialogContent dividers>
          <SpecialRequestDetailView request={detail} getStatusColor={getStatusColor} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(denyTarget)} onClose={() => setDenyTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('specialRequests.denyTitle')}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            label={t('specialRequests.statusNote')}
            value={denyNote}
            onChange={(e) => setDenyNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDenyTarget(null)} disabled={actionLoading}>{t('common.cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleDeny} disabled={actionLoading}>
            {t('specialRequests.actions.deny')}
          </Button>
        </DialogActions>
      </Dialog>

      <SpecialRequestFulfillDialog
        open={Boolean(fulfillTarget)}
        onClose={() => setFulfillTarget(null)}
        request={fulfillTarget}
        loading={actionLoading}
        onSubmit={handleFulfill}
      />

      <Dialog open={createOpen} onClose={() => { setCreateOpen(false); resetForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>{t('specialRequests.createForClientTitle')}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <AutocompleteField
              label={t('orders.client')}
              options={clients}
              value={createClient?._id ?? ''}
              onChange={(id) => setCreateClient(clients.find((c) => c._id === id) || null)}
              getOptionLabel={(opt) => `${opt.firstName || ''} ${opt.lastName || ''}`.trim() || opt.email || ''}
              getOptionValue={(u) => u._id}
              isOptionEqualToValue={(opt, val) => opt._id === val._id}
              size="small"
              required
            />
            <SelectField
              label={t('specialRequests.category')}
              value={form.category}
              onChange={(value) => setForm({
                ...DEFAULT_SPECIAL_REQUEST_FORM,
                category: value,
                details: { ...DEFAULT_SPECIAL_REQUEST_FORM.details },
              })}
              options={ALL_SPECIAL_REQUEST_CATEGORIES.map((value) => ({
                value,
                label: t(`specialRequests.categories.${value}`),
              }))}
              size="small"
            />
            <SpecialRequestFormFields
              form={form}
              onChange={setForm}
              filterOptions={filterOptions}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateOpen(false); resetForm(); }} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? t('common.saving') : t('specialRequests.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
